import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/videos", label: "Videos" },
  { to: "/guests", label: "Guests" },
  { to: "/community", label: "Community" },
  { to: "/partnerships", label: "Fairway Partners" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl backdrop-saturate-150" data-testid="site-navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center px-5 md:h-20 md:px-8">
        <nav className="hidden w-full items-center justify-center gap-6 lg:flex xl:gap-8" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-white ${
                  isActive || (l.to !== "/" && location.pathname.startsWith(l.to)) ? "text-white" : "text-muted"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="https://fore.createstreet.com/fthi/shop/home"
            data-testid="nav-link-shop"
            className="relative font-mono text-xs uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:text-white"
          >
            Shop
          </a>
          <Link
            to="/sponsor"
            data-testid="nav-sponsor-cta"
            className="bg-white px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
          >
            Sponsor Us
          </Link>
        </nav>

        <Link
          to="/"
          className="font-display text-sm font-extrabold uppercase leading-[1.05] tracking-tight lg:hidden"
          data-testid="nav-logo"
          onClick={() => setOpen(false)}
        >
          Fore The Hell <span className="text-brand-accent">Of It</span>
        </Link>
        <button
          className="ml-auto grid h-10 w-10 place-items-center text-white lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-ink lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-6">
              {[...LINKS, { to: "https://fore.createstreet.com/fthi/shop/home", label: "Shop", external: true }, { to: "/sponsor", label: "Sponsor Us" }].map((l) =>
                l.external ? (
                  <a
                    key={l.to}
                    href={l.to}
                    onClick={() => setOpen(false)}
                    data-testid="nav-mobile-shop"
                    className="border-b border-white/5 py-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white/80 transition-colors duration-200 hover:text-brand-accent"
                  >
                    {l.label}
                  </a>
                ) : (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="border-b border-white/5 py-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white/80 transition-colors duration-200 hover:text-brand-accent"
                  >
                    {l.label}
                  </NavLink>
                )
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
